// One token's history in the token panel: series and range keys (each range
// is its own request, from 30-minute buckets to daily), drawn by the shared
// ValueChart (U-32) with the token's daily net flows under the line. Honest
// about sparse data: fewer than two readings says so instead of drawing a
// dot, nulls are dropped rather than filled, and the caption repeats the
// response's own bucketing.

import { useMemo } from "react";

import { HISTORY_RANGES, type HistoryRange, type TokenHistory } from "../token-api";
import { SERIES, chartCaption, seriesPoints, seriesSpec, timeLabel, valueLabel, type SeriesKey } from "./history-chart";
import styles from "./TokenPanel.module.css";
import { ValueChart } from "./ValueChart";

export type HistoryStatus = { kind: "loading" } | { kind: "error"; message: string } | { kind: "ready"; history: TokenHistory };


function Keys<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (next: T) => void;
}) {
  return (
    <div aria-label={label} className={styles.keys} role="group">
      {options.map((option) => (
        <button aria-pressed={option.value === value} className={styles.key} key={option.value} onClick={() => onChange(option.value)} type="button">
          {option.label}
        </button>
      ))}
    </div>
  );
}

const RANGE_OPTIONS = HISTORY_RANGES.map((range) => ({ value: range, label: range === "all" ? "All" : range }));
const SERIES_OPTIONS = SERIES.map((spec) => ({ value: spec.key, label: spec.label }));

/** The drawn chart for one series with two or more readings. Keyed by its parent so hover resets with the data. */
export function HistoryChart({
  status,
  range,
  onRange,
  series,
  onSeries,
  flows,
}: {
  status: HistoryStatus;
  range: HistoryRange;
  onRange: (next: HistoryRange) => void;
  series: SeriesKey;
  onSeries: (next: SeriesKey) => void;
  /** Daily net flows, drawn as a bar strip under the line (U-32). */
  flows?: ReadonlyArray<readonly [number, number | null]>;
}) {
  const spec = seriesSpec(series);
  const history = status.kind === "ready" ? status.history : null;
  const points = useMemo(() => (history ? seriesPoints(history.series, series) : []), [history, series]);

  let body;
  if (status.kind === "loading") {
    body = <p className={styles.status}>Reading history…</p>;
  } else if (status.kind === "error") {
    body = (
      <p className={styles.status}>
        <span className={styles.error}>History unavailable right now: {status.message}</span>
      </p>
    );
  } else if (points.length < 2) {
    const name = spec.label.toLowerCase();
    const only = points[0];
    body = (
      <>
        <p className={styles.chartEmpty}>
          {status.history.observations === 0
            ? "No observations yet for this token. The first collection has not covered it."
            : only
              ? `Only one ${name} reading in this range (${timeLabel(only.t)} · ${valueLabel(only.v, spec.kind, true)}); a line needs two.`
              : `No ${name} recorded in any of the ${status.history.points} buckets in this range.`}
        </p>
        <p className={styles.caption}>{chartCaption(status.history, points.length)}</p>
      </>
    );
  } else {
    body = (
      <>
        <ValueChart
          exact={(v) => valueLabel(v, spec.kind, true)}
          exportName={`${status.history.token.symbol}-${series}-${range}`.toLowerCase()}
          flows={flows}
          format={(v) => valueLabel(v, spec.kind)}
          key={`${status.history.token.id}|${range}|${series}`}
          points={points.map((p): [number, number] => [p.t, p.v])}
          presets={null}
          title={`${status.history.token.symbol} ${spec.label.toLowerCase()}`}
          withTime={range !== "all"}
        />
        <p className={styles.caption}>{chartCaption(status.history, points.length)}</p>
      </>
    );
  }

  return (
    <div className={styles.chart}>
      <div className={styles.chartControls}>
        <Keys label="Series" onChange={onSeries} options={SERIES_OPTIONS} value={series} />
        <Keys label="Range" onChange={onRange} options={RANGE_OPTIONS} value={range} />
      </div>
      {body}
    </div>
  );
}
